import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaTimes, FaStar, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import PublicNav from '../../components/PublicNav/PublicNav';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import LoginModal from '../../components/LoginModal/LoginModal';
import './Pricing.css';

const Pricing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    console.log('Modal state changed - showLoginModal:', showLoginModal, 'selectedPlan:', selectedPlan);
  }, [showLoginModal, selectedPlan]);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/subscriptions/plans');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleSubscribe = async (planType) => {
    console.log('handleSubscribe called with planType:', planType);
    console.log('isAuthenticated:', isAuthenticated);
    //  setShowLoginModal(true);
    //  return;
    // Check if user is authenticated using AuthContext
    if (!isAuthenticated) {
      console.log('User not authenticated, showing login modal');
      setSelectedPlan(planType);
      setShowLoginModal(true);
      return;
    }

    console.log('User is authenticated, proceeding with subscription');

    if (planType === 'free') {
      toast.info('You are already on the free plan');
      return;
    }

    await processSubscription(planType);
  };

  const processSubscription = async (planType) => {
    setLoading(true);

    try {
      // Create order
      const orderResponse = await api.post('/subscriptions/create', {
        planType,
        billingCycle
      });

      const { order, key } = orderResponse.data;

      // Initialize Razorpay
      const options = {
        key: key,
        amount: order.amount,
        currency: order.currency,
        name: 'Project Portfolio Management',
        description: `${planType.charAt(0).toUpperCase() + planType.slice(1)} Plan - ${billingCycle}`,
        order_id: order.id,
        handler: async function (response) {
          try {
            // Verify payment
            await api.post('/subscriptions/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            toast.success('Subscription activated successfully!');
            navigate('/app/dashboard');
          } catch (error) {
            toast.error('Payment verification failed');
            console.error('Verification error:', error);
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: ''
        },
        theme: {
          color: '#667eea'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

      razorpay.on('payment.failed', function (response) {
        toast.error('Payment failed. Please try again.');
        console.error('Payment failed:', response.error);
      });

    } catch (error) {
      toast.error('Failed to create subscription order');
      console.error('Subscription error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    if (selectedPlan) {
      processSubscription(selectedPlan);
      setSelectedPlan(null);
    }
  };

  const pricingPlans = [
    {
      name: 'Free',
      type: 'free',
      price: { monthly: 0, yearly: 0 },
      popular: false,
      description: 'Perfect for individuals and small teams getting started',
      features: [
        { text: '3 Projects', included: true },
        { text: '5 Team Members', included: true },
        { text: '1GB Storage', included: true },
        { text: 'Basic Reports', included: true },
        { text: 'Community Support', included: true },
        { text: 'Advanced Analytics', included: false },
        { text: 'Priority Support', included: false },
        { text: 'Custom Integrations', included: false }
      ]
    },
    {
      name: 'Basic',
      type: 'basic',
      price: { monthly: 999, yearly: 9990 },
      popular: false,
      description: 'Great for growing teams and small businesses',
      features: [
        { text: '10 Projects', included: true },
        { text: '15 Team Members', included: true },
        { text: '10GB Storage', included: true },
        { text: 'Advanced Reports', included: true },
        { text: 'Email Support', included: true },
        { text: 'Time Tracking', included: true },
        { text: 'Priority Support', included: false },
        { text: 'Custom Integrations', included: false }
      ]
    },
    {
      name: 'Professional',
      type: 'professional',
      price: { monthly: 2999, yearly: 29990 },
      popular: true,
      description: 'Best for professional teams and agencies',
      features: [
        { text: '50 Projects', included: true },
        { text: '50 Team Members', included: true },
        { text: '100GB Storage', included: true },
        { text: 'Custom Reports', included: true },
        { text: 'Priority Support', included: true },
        { text: 'Advanced Analytics', included: true },
        { text: 'API Access', included: true },
        { text: 'Custom Integrations', included: false }
      ]
    },
    {
      name: 'Enterprise',
      type: 'enterprise',
      price: { monthly: 9999, yearly: 99990 },
      popular: false,
      description: 'For large organizations with advanced needs',
      features: [
        { text: 'Unlimited Projects', included: true },
        { text: 'Unlimited Team Members', included: true },
        { text: '1TB Storage', included: true },
        { text: 'Custom Everything', included: true },
        { text: '24/7 Dedicated Support', included: true },
        { text: 'Advanced Security', included: true },
        { text: 'Custom Integrations', included: true },
        { text: 'SLA Guarantee', included: true }
      ]
    }
  ];

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="pricing-page">
      <PublicNav />
      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="pricing-hero-content"
          >
            <h1 className="pricing-title">Simple, Transparent Pricing</h1>
            <p className="pricing-subtitle">
              Choose the plan that's right for your team. No hidden fees.
            </p>

            {/* Billing Toggle */}
            <div className="billing-toggle">
              <button
                className={billingCycle === 'monthly' ? 'active' : ''}
                onClick={() => setBillingCycle('monthly')}
              >
                Monthly
              </button>
              <button
                className={billingCycle === 'yearly' ? 'active' : ''}
                onClick={() => setBillingCycle('yearly')}
              >
                Yearly
                <span className="discount-badge">Save 17%</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="pricing-cards-section">
        <div className="container">
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                className={`pricing-card ${plan.popular ? 'popular' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                {plan.popular && (
                  <div className="popular-badge">
                    <FaStar /> Most Popular
                  </div>
                )}

                <div className="plan-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <div className="plan-price">
                    <span className="currency">₹</span>
                    <span className="amount">
                      {plan.price[billingCycle].toLocaleString('en-IN')}
                    </span>
                    <span className="period">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                  </div>

                  <p className="plan-description">{plan.description}</p>

                  {billingCycle === 'yearly' && plan.price.yearly > 0 && (
                    <p className="yearly-note">
                      {formatPrice(plan.price.yearly / 12)}/month billed annually
                    </p>
                  )}
                </div>

                <ul className="plan-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className={feature.included ? 'included' : 'not-included'}>
                      {feature.included ? (
                        <FaCheckCircle className="feature-icon check" />
                      ) : (
                        <FaTimesCircle className="feature-icon cross" />
                      )}
                      <span className="feature-text">{feature.text}</span>
                    </li>
                  ))}
                </ul>

                <button
                  className={`subscribe-btn ${plan.popular ? 'primary' : 'secondary'}`}
                  onClick={() => handleSubscribe(plan.type)}
                  disabled={loading}
                >
                  {plan.type === 'free' ? 'Get Started' : 'Subscribe Now'}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pricing-faq-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Frequently Asked Questions</h2>
          </motion.div>

          <div className="faq-grid">
            <motion.div
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h3 className="faq-question">Can I change plans later?</h3>
              <p className="faq-answer">
                Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
              </p>
            </motion.div>

            <motion.div
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="faq-question">What payment methods do you accept?</h3>
              <p className="faq-answer">
                We accept all major credit/debit cards, UPI, net banking, and wallets through Razorpay.
              </p>
            </motion.div>

            <motion.div
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="faq-question">Is there a free trial?</h3>
              <p className="faq-answer">
                Yes! All paid plans come with a 14-day free trial. No credit card required.
              </p>
            </motion.div>

            <motion.div
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="faq-question">Can I cancel anytime?</h3>
              <p className="faq-answer">
                Yes, you can cancel your subscription at any time. No questions asked.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      <PublicFooter />

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
      />
    </div>
  );
};

export default Pricing;


